import { Box, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  SIDEKICK_FEATURE_EVENT,
  type SidekickSuggestionPlacement,
  useSidekickFeatureEventReporter,
} from '../../featureEvents';
import type { SuggestionChipSet } from '../../state/chatTypes';
import { SIDEKICK_TRANSLATION_NAMESPACE } from '../../translations';
import SuggestionChip from '../SuggestionChip';
import SuggestionChipSkeleton from '../SuggestionChipSkeleton';
import useStyles from './PanelZeroStateSuggestions.styles';

const DEFAULT_LOADING_CHIP_COUNT = 3;

export type PanelZeroStateSuggestionsProps = {
  /**
   * Suggestion-chip dataset shared with the entry card. Question
   * chips prefer `chip.label` when supplied and fall back to
   * `chip.translationKey` resolved through the package's own
   * `SIDEKICK_TRANSLATION_NAMESPACE`.
   */
  chipSet: SuggestionChipSet;
  /**
   * Invoked with the resolved chip label whenever the user clicks a
   * question chip; returns whether the question was accepted. The
   * composing `<ChatInterface />` wires this to
   * `useConversation().sendMessage(text)`.
   */
  onSubmitQuestion: (text: string) => boolean;
  /**
   * When `true`, renders `loadingChipCount` skeleton pills in place
   * of the question chips. When enabled, the empty-state copy keeps
   * rendering so the swap-in to real chips introduces no layout
   * shift. Defaults to `false`.
   */
  isLoading?: boolean;
  /**
   * Number of `SuggestionChipSkeleton` instances to render while
   * `isLoading` is `true`. Defaults to `3` to mirror the production
   * suggestion-row size.
   */
  loadingChipCount?: number;
  /**
   * Whether to render the legacy explanatory copy above the suggestion
   * chips. Defaults to `true`.
   */
  showEmptyStateCopy?: boolean;
  /** Whether to use the compact M1 footer layout. */
  isM1Enabled?: boolean;
};

/**
 * Zero-state for the panel — optional empty-state copy + a row of
 * suggestion chips (no CTA).
 *
 * - While `isLoading` is `true`, renders `loadingChipCount` skeleton
 *   pills in the chip row.
 * - When `isLoading` is `false` and `chipSet.questions` is empty, the
 *   chip row collapses so loading-or-empty surfaces never show a
 *   blank pill placeholder.
 */
function PanelZeroStateSuggestions({
  chipSet,
  onSubmitQuestion,
  isLoading = false,
  loadingChipCount = DEFAULT_LOADING_CHIP_COUNT,
  showEmptyStateCopy = true,
  isM1Enabled = false,
}: PanelZeroStateSuggestionsProps) {
  const { classes } = useStyles({ isM1Enabled });
  const { t: tPackage } = useTranslation(SIDEKICK_TRANSLATION_NAMESPACE);
  const { emitOnQuestionSent } = useSidekickFeatureEventReporter();

  const handleQuestionClick = useCallback(
    (resolvedLabel: string, placement: SidekickSuggestionPlacement) => {
      emitOnQuestionSent(
        {
          type: SIDEKICK_FEATURE_EVENT.SUGGESTION_SELECTED,
          surface: 'panel',
          ...placement,
        },
        {
          question: resolvedLabel,
          submit: () => onSubmitQuestion(resolvedLabel),
        }
      );
    },
    [emitOnQuestionSent, onSubmitQuestion]
  );

  const questionChipsById = useMemo(() => {
    const chipCount = chipSet.questions.length;
    return Object.fromEntries(
      chipSet.questions.map((q, index) => {
        const label: string =
          q.label !== undefined ? q.label : tPackage(q.translationKey);
        const placement = { chipPosition: index + 1, chipCount };
        return [
          q.id,
          { label, onClick: () => handleQuestionClick(label, placement) },
        ] as const;
      })
    ) as Record<string, { label: string; onClick: () => void }>;
  }, [chipSet.questions, handleQuestionClick, tPackage]);

  const skeletonKeys = useMemo(
    () =>
      Array.from(
        { length: Math.max(0, loadingChipCount) },
        (_, index) => `chip-skeleton-${index}`
      ),
    [loadingChipCount]
  );

  const hasQuestions = chipSet.questions.length > 0;

  return (
    <Box
      className={classes.container}
      data-testid="ai-sidekick-panel-zero-state"
    >
      {showEmptyStateCopy ? (
        <Box className={classes.topRegion}>
          <Typography className={classes.copy} component="p">
            {tPackage('chat.emptyState.copy')}
          </Typography>
        </Box>
      ) : null}
      {(isLoading || hasQuestions) && (
        <Box className={classes.chipsRow}>
          {isLoading
            ? skeletonKeys.map((key) => <SuggestionChipSkeleton key={key} />)
            : chipSet.questions.map(({ id }) => {
                const { label, onClick } = questionChipsById[id];
                return (
                  <SuggestionChip
                    key={id}
                    id={id}
                    label={label}
                    onClick={onClick}
                  />
                );
              })}
        </Box>
      )}
    </Box>
  );
}

export default PanelZeroStateSuggestions;
